import { useState } from "react";
import { Link } from "react-router-dom";
import { LandingHeader, LandingFooter } from "@/components/landing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Clock, ChevronRight, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

// Schema de validation Zod pour le formulaire de contact
const contactSchema = z.object({
  nom: z.string().min(1, "Le nom est requis").max(100, "100 caractères max"),
  email: z.string().min(1, "L'email est requis").email("Adresse email invalide"),
  entreprise: z.string().max(150, "150 caractères max").optional(),
  sujet: z.enum(
    ['fonctionnalites', 'tarifs', 'technique', 'demo', 'partenariat', 'autre'] as const,
    { message: "Veuillez sélectionner un sujet" }
  ),
  message: z.string().min(1, "Le message est requis").max(5000, "5000 caractères max"),
  consent: z.literal(true, { errorMap: () => ({ message: "Veuillez accepter la politique de confidentialité" }) }),
});

const Contact = () => {
  usePageMeta({
    title: "Contact — B8ild",
    description: "Contactez l'équipe B8ild pour toute question sur notre logiciel de gestion de chantier BTP.",
    url: "https://b8ild.fr/contact",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    entreprise: "",
    sujet: "",
    message: "",
    consent: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation Zod
    const validation = contactSchema.safeParse({
      ...formData,
      entreprise: formData.entreprise || undefined,
      sujet: formData.sujet || undefined,
    });

    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast.error(issue.message);
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("contact", {
        body: {
          nom: formData.nom,
          email: formData.email,
          entreprise: formData.entreprise || null,
          sujet: formData.sujet,
          message: formData.message,
        },
      });

      if (error) throw error;

      setIsSuccess(true);
      toast.success("Message envoyé avec succès !");

      // Reset form
      setFormData({
        nom: "",
        email: "",
        entreprise: "",
        sujet: "",
        message: "",
        consent: false,
      });
    } catch (error) {
      console.error("Erreur envoi message:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact B8ild",
        url: "https://b8ild.fr/contact",
        description: "Contactez l'équipe B8ild pour toute question sur le logiciel de gestion chantier BTP.",
        inLanguage: "fr",
      }} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-12 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            Contactez-nous
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/60">
            Une question ? Un besoin ? Notre équipe vous répond sous 24-48h.
          </p>
        </div>
      </section>

      {/* Contenu */}
      <section className="flex-1 max-w-6xl mx-auto px-4 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Colonne gauche : Formulaire */}
          <div className="bg-[#1E293B] rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-semibold text-white mb-6">
              Envoyez-nous un message
            </h2>

            {isSuccess ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Message envoyé !
                </h3>
                <p className="text-white/60 mb-6">
                  Nous vous répondrons dans les plus brefs délais.
                </p>
                <Button
                  onClick={() => setIsSuccess(false)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-white">
                    Nom complet <span className="text-[#F59E0B]">*</span>
                  </Label>
                  <Input
                    id="nom"
                    placeholder="Jean Dupont"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="bg-[#0F172A] border-white/10 text-white placeholder:text-white/40 focus:border-[#F59E0B]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">
                    Email <span className="text-[#F59E0B]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jean@entreprise.fr"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#0F172A] border-white/10 text-white placeholder:text-white/40 focus:border-[#F59E0B]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entreprise" className="text-white">
                    Entreprise
                  </Label>
                  <Input
                    id="entreprise"
                    placeholder="Nom de votre entreprise (optionnel)"
                    value={formData.entreprise}
                    onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                    className="bg-[#0F172A] border-white/10 text-white placeholder:text-white/40 focus:border-[#F59E0B]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sujet" className="text-white">
                    Sujet <span className="text-[#F59E0B]">*</span>
                  </Label>
                  <Select
                    value={formData.sujet}
                    onValueChange={(value) => setFormData({ ...formData, sujet: value })}
                    required
                  >
                    <SelectTrigger className="bg-[#0F172A] border-white/10 text-white focus:border-[#F59E0B]">
                      <SelectValue placeholder="Sélectionnez un sujet" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10">
                      <SelectItem value="fonctionnalites" className="text-white hover:bg-white/10">
                        Question sur les fonctionnalités
                      </SelectItem>
                      <SelectItem value="tarifs" className="text-white hover:bg-white/10">
                        Question sur les tarifs
                      </SelectItem>
                      <SelectItem value="technique" className="text-white hover:bg-white/10">
                        Problème technique
                      </SelectItem>
                      <SelectItem value="demo" className="text-white hover:bg-white/10">
                        Demande de démonstration
                      </SelectItem>
                      <SelectItem value="partenariat" className="text-white hover:bg-white/10">
                        Partenariat
                      </SelectItem>
                      <SelectItem value="autre" className="text-white hover:bg-white/10">
                        Autre
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white">
                    Message <span className="text-[#F59E0B]">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Décrivez votre demande en détail..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-[#0F172A] border-white/10 text-white placeholder:text-white/40 focus:border-[#F59E0B] resize-none"
                    required
                  />
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, consent: checked as boolean })
                    }
                    className="mt-1 border-white/30 data-[state=checked]:bg-[#F59E0B] data-[state=checked]:border-[#F59E0B]"
                  />
                  <Label htmlFor="consent" className="text-sm text-white/60 leading-relaxed cursor-pointer">
                    J'accepte que mes données soient utilisées pour traiter ma demande conformément à la{" "}
                    <Link to="/confidentialite" className="text-[#F59E0B] hover:underline">
                      Politique de Confidentialité
                    </Link>{" "}
                    <span className="text-[#F59E0B]">*</span>
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold h-11"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    "Envoyer le message"
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* Colonne droite : Informations */}
          <div className="space-y-6">
            {/* Autres moyens de contact */}
            <div className="bg-[#1E293B] rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Autres moyens de nous contacter
              </h2>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#F59E0B] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <a
                      href="mailto:contact@b8ild.fr"
                      className="text-white/60 hover:text-[#F59E0B] transition-colors"
                    >
                      contact@b8ild.fr
                    </a>
                    <p className="text-sm text-white/40 mt-1">Réponse sous 24-48h ouvrées</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#F59E0B] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Horaires de support</p>
                    <p className="text-white/60">Lundi - Vendredi</p>
                    <p className="text-white/60">9h00 - 18h00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions fréquentes */}
            <div className="bg-[#1E293B] rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Questions fréquentes
              </h2>

              <p className="text-white/60 mb-4">
                Avant de nous contacter, consultez peut-être notre FAQ :
              </p>

              <ul className="space-y-2">
                <li>
                  <Link
                    to="/#faq"
                    className="text-[#F59E0B] hover:underline flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    Comment fonctionne l'essai gratuit ?
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#faq"
                    className="text-[#F59E0B] hover:underline flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    Comment importer mes factures ?
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#faq"
                    className="text-[#F59E0B] hover:underline flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    Comment changer de formule ?
                  </Link>
                </li>
              </ul>

              <Button
                variant="outline"
                className="w-full mt-4 border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
                asChild
              >
                <Link to="/#faq">Voir toute la FAQ</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Contact;
